'use client';

import { Link } from "@heroui/link";
import clsx from "clsx";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import './HeaderWithTabs.css'
import Image from "next/image";
import ThreeDotsMenu from "../icons/ThreeDotsMenu";
import UserProfile from "./UserProfile";
import { useUserLocale } from "@/hooks/useUserLocale";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { usePathname, useRouter } from "next/navigation";
import { usersDataTools } from "@/lib/data/users-data-tools";
import { getCurrentBearerToken } from "@/lib/client-auth";
import { Spacer } from "@heroui/spacer";

function AreaLink({children, to, className = "", isCurrent = false, locale, showDot = false, disabled = false}: {children: React.ReactNode, to: string, className?: string, isCurrent?: boolean, locale?: string, showDot?: boolean, disabled?: boolean}) {
    // Locale may optinally influence the text size
    return <Link className={clsx(
        "flex flex-col",
        "area-link text-[0.83rem] sm:text-[0.89rem] font-semibold break-keep whitespace-nowrap",
        isCurrent ? 'text-secondary underline decoration-dashed underline-offset-4' : 'text-gray-800',
        disabled ? 'opacity-50 text-gray-500 cursor-not-allowed' : '',
        className
    )} href={disabled ? '#' : to}>
        <AnimatePresence>
            {showDot && (
                <motion.div 
                    className="h-[0px]"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ 
                        type: "spring",
                        stiffness: 400,
                        damping: 10,
                        duration: 0.6
                    }}
                >
                    <span className="text-pink-500 relative top-[-9px]">
                        <ExclamationCircleIcon className="w-3 h-3 stroke-2" />
                    </span>
                </motion.div>
            )}
        </AnimatePresence>
        <span className={clsx(showDot ? 'animate-blink' : '')}>{children}</span>
    </Link>
}

export default function HeaderWithTabs({showTabs = true}: {showTabs?: boolean}) {
    const pathname = usePathname();
    
    const [scrolled, setScrolled] = useState(false);
    const [shouldHide, setShouldHide] = useState(false);
    const [hiddenOnScrollDown, setHiddenOnScrollDown] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);
    const isMobileScreen = useSelector((state: any) => state.app.isMobileScreen);
    // const isFlutterWebview = useSelector((state: any) => state.app.isFlutterWebview);
    const headerTabs = useSelector((state: any) => state.app.headerTabs);
    const screenInfo = useSelector((state: any) => state.nav.screenInfo);
    const router = useRouter();
    const { t, locale } = useUserLocale();
    const [safeArea, setSafeArea] = useState<{top: number, bottom: number}>({top: 0, bottom: 0});
    const currentUser = useSelector((state: any) => state.app.currentUser);    

    const isGuestUser = usersDataTools.isGuestUser(currentUser || null);

    const handleLogoTap = () => {
        router.push('/');
    }

    const activeScreenInfo = useMemo(() => {
        return screenInfo?.path === pathname ? screenInfo : null;
    }, [screenInfo, pathname]);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            const scrollDelta = currentScrollY - lastScrollY;
            
            // Update scrolled state for background effect
            const isScrolled = isMobileScreen ? currentScrollY > 10 : currentScrollY > 30;
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }

            // Hide header when scrolling down more than 100px from the top
            if (scrollDelta > 0 && currentScrollY > 100) {
                if (!hiddenOnScrollDown) {
                    setHiddenOnScrollDown(true);
                }
            }
            // Show header when scrolling up more than 5px
            else if (scrollDelta < -5) {
                if (hiddenOnScrollDown) {
                    setHiddenOnScrollDown(false);
                }
            }

            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [scrolled, isMobileScreen, lastScrollY, hiddenOnScrollDown]);

    useEffect(() => {
        setTimeout(() => {
            const safeAreaTop = document.documentElement.style.getPropertyValue('--safe-area-top');
    
            if (safeAreaTop) {
                let magicAdjustment = 0;
                
                if (parseInt(safeAreaTop) > 10) {
                    magicAdjustment = 50; // only needed for iOS? TODO: investigate
                }

                setSafeArea({top: parseInt(safeAreaTop) + magicAdjustment, bottom: 0});
            }
        }, 1000);
    }, []);

    const logoVersion = useMemo(() => {
        return isGuestUser ? 'logo-web-lush-anon-sm' : 'logo-web-lush-sm';
    }, [isGuestUser]);

    return (
        <header
        className={clsx(
            'fixed top-0 left-0 header-tabbed-adaptive-height right-0 z-20 transition-all duration-300 ease-in-out',
            'md:h-14',
            'max-w-3xl mx-auto',
            'grid grid-cols-3 items-center',
            hiddenOnScrollDown && 'translate-y-[-100%]',
            scrolled ? 'bg-white/90 shadow-lg shadow-white dark:shadow-neutral-900 dark:bg-neutral-900/95' : 'bg-transparent dark:bg-transparent'                 
        )} style={{ display: shouldHide ? 'none' : 'grid' }}>
            {/* Logo - Left column */}
            <div className="flex items-center gap-2 ml-2 justify-self-start cursor-pointer">
                <div className="p-0" onClick={handleLogoTap}>
                    <Image src={`/images/${logoVersion}.png`} alt="Chatlanga Logo" className="w-[39px] h-[39px] md:w-[48px] md:h-[48px]" width={75} height={75} priority={false} />
                </div>
                {isGuestUser && <div className="ml-[-1.2rem] text-[0.65rem] text-gray-100 px-1 py-0.5 bg-gradient-to-br from-pink-500 via-rose-500 to-purple-600 rounded-xl shadow-md">Visitor</div>}
            </div>

            {/* Main UI Tab links - Middle column (absolutely centered) */}
            {showTabs && <div className="flex flex-row items-center justify-center gap-3 sm:gap-4 justify-self-center">
                {headerTabs.map((tab) => {
                    let tabDisabled = false;

                    // if ((tab.id === 'workbook' || tab.id === 'contacts') && isGuestUser) {
                    //     tabDisabled = true;
                    // }

                    return <AreaLink to={tab.to} locale={locale} key={tab.id} isCurrent={tab.active} showDot={tab.showDot} disabled={tabDisabled}>{t.nav.header.tabs[tab.label]}</AreaLink>
                })}
            </div>}
            
            {!showTabs && activeScreenInfo && <div className="text-center font-medium">{activeScreenInfo.title}</div>}
            {!showTabs && !activeScreenInfo && <Spacer />}

            {/* Three dots - Right column (for balance) */}
            <div className="justify-self-end mr-2">
                <UserProfile />
                {/* <div className="flex items-center gap-2" onClick={handleMenuIconTap}>
                    <ThreeDotsMenu open={false} />
                </div> */}
            </div>
        </header>
    );
}